import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import { Team } from '../../../data/TeamDataProvider';
import GenericFlyoutPanel from '../../common/GenericFlyoutPanel/GenericFlyoutPanel';
import FormGenerator, { FormFieldConfig } from '../../common/FormGenerator/FormGenerator';
import { FaEdit } from 'react-icons/fa';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';

interface TeamGeneralProps {
  team: Team;
  onEdit: (data: Partial<Team>) => Promise<void>;
}

const TeamGeneral: React.FC<TeamGeneralProps> = ({ team, onEdit }) => {
  const { t } = useTranslation(['common', 'screens']);
  const [showEdit, setShowEdit] = useState(false);

  const teamFields: FormFieldConfig[] = useMemo(() => [
    { key: 'name', label: t('screens:teamGeneral.fields.teamName'), type: 'string', required: true },
    { key: 'description', label: t('screens:teamGeneral.fields.description'), type: 'string' },
  ], [t]);

  const handleEdit = async (data: Partial<Team>) => {
    await onEdit({ ...team, ...data });
    setShowEdit(false);
  };

  return (
    <SubsectionPanel
      title={t('screens:teamGeneral.title')}
      actions={
        <RoleGuard roles={UserRoles.Admin + "," + UserRoles.Manager}>
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowEdit(true)}>
            <FaEdit /> {t('screens:teamGeneral.editButton')}
          </button>
        </RoleGuard>
      }
    >
      <div className="row mb-2">
        <div className="col-3 fw-bold">{t('screens:teamGeneral.labelName')}</div>
        <div className="col-9">{team.name}</div>
      </div>
      <div className="row mb-2">
        <div className="col-3 fw-bold">{t('screens:teamGeneral.labelDescription')}</div>
        <div className="col-9">{team.description || <span className="text-muted">{t('screens:teamGeneral.noDescription')}</span>}</div>
      </div>
      <GenericFlyoutPanel open={showEdit} onClose={() => setShowEdit(false)} title={t('screens:teamGeneral.editButton')}>
        <FormGenerator
          fields={teamFields}
          initialData={team}
          onSubmit={handleEdit}
          submitLabel={t('common:save')}
        />
      </GenericFlyoutPanel>
    </SubsectionPanel>
  );
};

export default TeamGeneral;
